import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const VAPID_PUBLIC_KEY =
  "BPu7q64XhRmrfVgVz2Y-MafMIF6wKlnXh0XNJp0e1xFChah66dV02SvKAOgUMe_EXi9quC1HG62paKvaozMnKyc";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Auto-registers the browser for Web Push when the user is authenticated and
 * has previously granted notification permission. Safe to call on every render.
 */
export function useWebPushSubscription() {
  useEffect(() => {
    const subscribe = async () => {
      try {
        if (
          typeof window === "undefined" ||
          !("serviceWorker" in navigator) ||
          !("PushManager" in window) ||
          !("Notification" in window)
        ) {
          return;
        }

        // Skip in iframes (preview environments)
        const isInIframe = (() => {
          try { return window.self !== window.top; } catch { return true; }
        })();
        if (isInIframe) return;

        // Only auto-subscribe if permission is already granted.
        // Manual enable lives in NotificationSettings via usePushNotifications.
        if (Notification.permission !== "granted") return;

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const registration = await navigator.serviceWorker.ready;

        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
          });
        }

        const p256dhKey = subscription.getKey("p256dh");
        const authKey = subscription.getKey("auth");
        if (!p256dhKey || !authKey) return;

        await supabase.from("push_subscriptions").upsert(
          {
            user_id: user.id,
            endpoint: subscription.endpoint,
            p256dh: arrayBufferToBase64Url(p256dhKey),
            auth: arrayBufferToBase64Url(authKey),
          },
          { onConflict: "user_id,endpoint" }
        );
      } catch (err) {
        console.error("[WebPush] auto-subscribe failed:", err);
      }
    };

    subscribe();
  }, []);
}
